import { type RemoteTimezone } from '@/lib/utils/dates';
import {
  type Campus,
  type CourseTrack,
  type UrlEntry,
  type UrlRegion,
} from '@/types';

import type { PageParams, PdpParamsParsed } from './page';

export type PdpPages = `pdp/${CourseTrack}/${Campus}` &
  'pdp/ai/rmt' &
  'pdp/dv/rmt' &
  'pdp/mk/rmt' &
  'pdp/ml/rmt';

export const urls: Record<PdpPages, Partial<Record<UrlRegion, UrlEntry>>> = {
  // Artificial Intelligence
  'pdp/ai/ber': {
    de: 'kunstliche-intelligenz/berlin',
    'de-en': 'artificial-intelligence/berlin',
  },
  'pdp/ai/rmt': {
    de: 'kunstliche-intelligenz/remote',
    'de-en': 'artificial-intelligence/remote',
    es: 'inteligencia-artificial/remoto',
    'es-en': 'artificial-intelligence/remote',
    fr: 'intelligence-artificielle/a-distance',
    'fr-en': 'artificial-intelligence/remote',
    gb: 'artificial-intelligence/remote',
    nl: 'kunstmatige-intelligentie/remote',
    'nl-en': 'artificial-intelligence/remote',
    pt: 'inteligencia-artificial/remoto',
    'pt-en': 'artificial-intelligence/remote',
    ww: 'artificial-intelligence/remote',
  },

  // Cybersecurity
  'pdp/cy/ams': {
    nl: 'cybersecurity/amsterdam',
    'nl-en': 'cybersecurity/amsterdam',
  },
  'pdp/cy/bcn': {
    es: 'ciberseguridad/barcelona',
    'es-en': 'cybersecurity/barcelona',
  },
  'pdp/cy/ber': {
    de: 'cybersecurity/berlin',
    'de-en': 'cybersecurity/berlin',
  },
  'pdp/cy/lis': {
    pt: 'ciberseguranca/lisboa',
    'pt-en': 'cybersecurity/lisbon',
  },
  'pdp/cy/lon': {
    gb: 'cybersecurity/london',
  },
  'pdp/cy/mad': {
    es: 'ciberseguridad/madrid',
    'es-en': 'cybersecurity/madrid',
  },
  'pdp/cy/par': {
    fr: 'cybersecurite/paris',
    'fr-en': 'cybersecurity/paris',
  },
  'pdp/cy/rmt': {
    de: 'cybersecurity/remote',
    'de-en': 'cybersecurity/remote',
    es: 'ciberseguridad/remoto',
    'es-en': 'cybersecurity/remote',
    fr: 'cybersecurite/a-distance',
    'fr-en': 'cybersecurity/remote',
    gb: 'cybersecurity/remote',
    nl: 'cybersecurity/remote',
    'nl-en': 'cybersecurity/remote',
    pt: 'ciberseguranca/remoto',
    'pt-en': 'cybersecurity/remote',
    ww: 'cybersecurity/remote',
  },

  // Data analytics
  'pdp/da/ams': {
    nl: 'data-analytics/amsterdam',
    'nl-en': 'data-analytics/amsterdam',
  },
  'pdp/da/bcn': {
    es: 'data-analytics/barcelona',
    'es-en': 'data-analytics/barcelona',
  },
  'pdp/da/ber': {
    de: 'data-analytics/berlin',
    'de-en': 'data-analytics/berlin',
  },
  'pdp/da/lis': {
    pt: 'data-analytics/lisboa',
    'pt-en': 'data-analytics/lisbon',
  },
  'pdp/da/lon': {
    gb: 'data-analytics/london',
  },
  'pdp/da/mad': {
    es: 'data-analytics/madrid',
    'es-en': 'data-analytics/madrid',
  },
  'pdp/da/par': {
    fr: 'data-analytics/paris',
    'fr-en': 'data-analytics/paris',
  },
  'pdp/da/rmt': {
    de: 'data-analytics/remote',
    'de-en': 'data-analytics/remote',
    es: 'data-analytics/remoto',
    'es-en': 'data-analytics/remote',
    fr: 'data-analytics/a-distance',
    'fr-en': 'data-analytics/remote',
    gb: 'data-analytics/remote',
    nl: 'data-analytics/remote',
    'nl-en': 'data-analytics/remote',
    pt: 'data-analytics/remoto',
    'pt-en': 'data-analytics/remote',
    us: 'data-analytics/remote',
    ww: 'data-analytics/remote',
  },

  // Devops & Cloud Computing
  'pdp/dv/rmt': {
    de: 'devops-cloud-computing/remote',
    'de-en': 'devops-cloud-computing/remote',
    es: 'devops-cloud-computing/remoto',
    'es-en': 'devops-cloud-computing/remote',
    fr: 'devops-cloud-computing/a-distance',
    'fr-en': 'devops-cloud-computing/remote',
    gb: 'devops-cloud-computing/remote',
    nl: 'devops-cloud-computing/remote',
    'nl-en': 'devops-cloud-computing/remote',
    pt: 'devops-cloud-computing/remoto',
    'pt-en': 'devops-cloud-computing/remote',
    ww: 'devops-cloud-computing/remote',
  },

  // Digital Marketing
  'pdp/mk/rmt': {
    de: 'digital-marketing/remote',
    'de-en': 'digital-marketing/remote',
    es: 'marketing-digital/remoto',
    'es-en': 'digital-marketing/remote',
    fr: 'marketing-digital/a-distance',
    'fr-en': 'digital-marketing/remote',
    gb: 'digital-marketing/remote',
    nl: 'digital-marketing/remote',
    'nl-en': 'digital-marketing/remote',
    pt: 'marketing-digital/remoto',
    'pt-en': 'digital-marketing/remote',
    ww: 'digital-marketing/remote',
  },

  // Data Science & Machine Learning
  'pdp/ml/rmt': {
    de: 'data-science-machine-learning/remote',
    'de-en': 'data-science-machine-learning/remote',
    es: 'data-science-machine-learning/remoto',
    'es-en': 'data-science-machine-learning/remote',
    fr: 'data-science-machine-learning/a-distance',
    'fr-en': 'data-science-machine-learning/remote',
    gb: 'data-science-machine-learning/remote',
    nl: 'data-science-machine-learning/remote',
    'nl-en': 'data-science-machine-learning/remote',
    pt: 'data-science-machine-learning/remoto',
    'pt-en': 'data-science-machine-learning/remote',
    ww: 'data-science-machine-learning/remote',
  },

  // UX/UI design
  'pdp/ux/ams': {
    nl: 'ux-ui-design/amsterdam',
    'nl-en': 'ux-ui-design/amsterdam',
  },
  'pdp/ux/bcn': {
    es: 'diseno-ux-ui/barcelona',
    'es-en': 'ux-ui-design/barcelona',
  },
  'pdp/ux/ber': {
    de: 'ux-ui-design/berlin',
    'de-en': 'ux-ui-design/berlin',
  },
  'pdp/ux/lis': {
    pt: 'ux-ui-design/lisboa',
    'pt-en': 'ux-ui-design/lisbon',
  },
  'pdp/ux/lon': {
    gb: 'ux-ui-design/london',
  },
  'pdp/ux/mad': {
    es: 'diseno-ux-ui/madrid',
    'es-en': 'ux-ui-design/madrid',
  },
  'pdp/ux/par': {
    fr: 'ux-ui-design/paris',
    'fr-en': 'ux-ui-design/paris',
  },
  'pdp/ux/rmt': {
    de: 'ux-ui-design/remote',
    'de-en': 'ux-ui-design/remote',
    es: 'diseno-ux-ui/remoto',
    'es-en': 'ux-ui-design/remote',
    fr: 'ux-ui-design/a-distance',
    'fr-en': 'ux-ui-design/remote',
    gb: 'ux-ui-design/remote',
    nl: 'ux-ui-design/remote',
    'nl-en': 'ux-ui-design/remote',
    pt: 'ux-ui-design/remoto',
    'pt-en': 'ux-ui-design/remote',
    us: 'ux-ui-design/remote',
    ww: 'ux-ui-design/remote',
  },

  // Web development
  'pdp/wd/ams': {
    nl: 'web-development/amsterdam',
    'nl-en': 'web-development/amsterdam',
  },
  'pdp/wd/bcn': {
    es: 'desarrollo-web/barcelona',
    'es-en': 'web-development/barcelona',
  },
  'pdp/wd/ber': {
    de: 'webentwicklung/berlin',
    'de-en': 'web-development/berlin',
  },
  'pdp/wd/lis': {
    pt: 'programacao-web/lisboa',
    'pt-en': 'web-development/lisbon',
  },
  'pdp/wd/lon': {
    gb: 'web-development/london',
  },
  'pdp/wd/mad': {
    es: 'desarrollo-web/madrid',
    'es-en': 'web-development/madrid',
  },
  'pdp/wd/par': {
    fr: 'developpement-web/paris',
    'fr-en': 'web-development/paris',
  },
  'pdp/wd/rmt': {
    de: 'webentwicklung/remote',
    'de-en': 'web-development/remote',
    es: 'desarrollo-web/remoto',
    'es-en': 'web-development/remote',
    fr: 'developpement-web/a-distance',
    'fr-en': 'web-development/remote',
    gb: 'web-development/remote',
    nl: 'web-development/remote',
    'nl-en': 'web-development/remote',
    pt: 'programacao-web/remoto',
    'pt-en': 'web-development/remote',
    us: 'web-development/remote',
    ww: 'web-development/remote',
  },
};

export const getPdpType = (
  params: Pick<PageParams, 'campus' | 'course'>
): PdpPages => {
  const { campus, course } = params;
  return `pdp/${course}/${campus}` as PdpPages;
};

export const parseParams = (params: PageParams) => {
  const { extraParams = [], ...initialParsedParams } = params;

  return (extraParams as string[]).reduce<PdpParamsParsed>(
    (parsedParams, pathPart, index) => {
      switch (pathPart) {
        case 'timezone':
          return {
            ...parsedParams,
            timezone: extraParams[index + 1].replace(
              '-',
              '/'
            ) as RemoteTimezone,
          };
        case 'e':
          return {
            ...parsedParams,
            exprimentId: extraParams[index + 1],
            variantId: extraParams[index + 2],
          };
        case 'user-country':
          return {
            ...parsedParams,
            userCountry: extraParams[index + 1],
          };
        default:
          return parsedParams;
      }
    },
    initialParsedParams
  );
};
